import React, { useState, useEffect, useCallback } from 'react';
import { formatDateLocale, formatDateTimeLocale } from '../utils/dateFormat';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  HeartPulse, CheckCircle, AlertTriangle, Clock, 
  ChevronDown, ChevronUp, Plus, MessageSquare,
  RefreshCw, User, Calendar, Star
} from 'lucide-react';
import api from '../utils/api';

const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700', icon: HeartPulse },
  completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle },
  escalated: { label: 'Escalated', color: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

const STEP_STATUS = {
  pending: { label: 'Pending', color: 'bg-gray-100 text-gray-600' },
  completed: { label: 'Done', color: 'bg-emerald-100 text-emerald-700' },
  skipped: { label: 'Skipped', color: 'bg-amber-100 text-amber-700' },
};

// ── Follow-up Step Row ──
const FollowupStep = ({ step, canManage, onComplete }) => {
  const { t } = useTranslation();
  const cfg = STEP_STATUS[step.status] || STEP_STATUS.pending;
  const targetDate = step.target_date ? new Date(step.target_date) : null;
  const isOverdue = targetDate && step.status === 'pending' && targetDate < new Date();

  return (
    <div className={`flex items-center justify-between py-2 px-3 rounded-md ${isOverdue ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`} data-testid={`step-${step.id}`}>
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${step.status === 'completed' ? 'bg-emerald-500' : step.status === 'skipped' ? 'bg-amber-500' : isOverdue ? 'bg-red-500' : 'bg-gray-300'}`} />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{step.name}</p>
          {step.description && <p className="text-xs text-gray-500 truncate">{step.description}</p>}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        {targetDate && (
          <span className={`text-xs ${isOverdue ? 'text-red-600 font-semibold' : 'text-gray-500'}`}>
            <Calendar className="h-3 w-3 inline mr-1" />
            {formatDateLocale(targetDate)}
          </span>
        )}
        <Badge className={`${cfg.color} text-[10px]`}>{cfg.label}</Badge>
        {canManage && step.status === 'pending' && (
          <Button size="sm" variant="outline" className="h-6 text-xs px-2" onClick={() => onComplete(step.id)} data-testid={`complete-step-${step.id}`}>
            <CheckCircle className="h-3 w-3 mr-1" /> Done
          </Button>
        )}
      </div>
    </div>
  );
};

// ── Note Item ──
const NoteItem = ({ note }) => (
  <div className="bg-gray-50 rounded-md p-2 text-sm" data-testid={`note-${note.id}`}>
    <div className="flex items-center gap-2 mb-1">
      <Badge variant="outline" className="text-[9px] h-4">{note.type}</Badge>
      <span className="text-xs text-gray-500">{note.author_role}</span>
      <span className="text-xs text-gray-400">{formatDateTimeLocale(note.created_at)}</span>
    </div>
    <p className="text-xs text-gray-700">{note.content}</p>
  </div>
);

// ── Complication Item ──
const ComplicationItem = ({ comp }) => {
  const sevColor = comp.severity === 'critical' ? 'bg-red-600' : comp.severity === 'high' ? 'bg-red-400' : comp.severity === 'medium' ? 'bg-amber-400' : 'bg-gray-400';
  return (
    <div className="flex items-start gap-2 bg-red-50 rounded-md p-2" data-testid={`complication-${comp.id}`}>
      <AlertTriangle className="h-4 w-4 text-red-500 mt-0.5 flex-shrink-0" />
      <div>
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${sevColor}`} />
          <span className="text-xs font-medium capitalize">{comp.severity}</span>
          <span className="text-xs text-gray-400">{formatDateTimeLocale(comp.reported_at)}</span>
        </div>
        <p className="text-xs text-gray-700 mt-1">{comp.description}</p>
      </div>
    </div>
  );
};

// ── Main Aftercare Card ──
export const AftercareCard = ({ record, canManage, onRefresh }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [noteText, setNoteText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const statusCfg = STATUS_CONFIG[record.status] || STATUS_CONFIG.active;
  const StatusIcon = statusCfg.icon;
  const completedSteps = (record.followup_plan || []).filter(s => s.status === 'completed').length;
  const totalSteps = (record.followup_plan || []).length;

  const handleCompleteStep = async (stepId) => {
    try {
      await api.put(`/api/aftercare/${record.id}/step`, { step_id: stepId, status: 'completed' });
      onRefresh?.();
    } catch (err) {
      console.error('Failed to complete step:', err);
    }
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setSubmitting(true);
    try {
      await api.post(`/api/aftercare/${record.id}/notes`, { content: noteText, type: 'general' });
      setNoteText('');
      onRefresh?.();
    } catch (err) {
      console.error('Failed to add note:', err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card className="border-l-4" style={{ borderLeftColor: record.status === 'escalated' ? '#ef4444' : record.status === 'completed' ? '#3b82f6' : '#10b981' }} data-testid={`aftercare-card-${record.id}`}>
      <CardHeader className="py-3 px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <StatusIcon className="h-5 w-5" style={{ color: record.status === 'escalated' ? '#ef4444' : '#10b981' }} />
            <div>
              <CardTitle className="text-sm">{record.treatment_name || 'Aftercare'}</CardTitle>
              <div className="flex items-center gap-2 mt-0.5">
                <Badge className={`${statusCfg.color} text-[10px]`}>{statusCfg.label}</Badge>
                <span className="text-xs text-gray-500">{completedSteps}/{totalSteps} steps</span>
                {record.satisfaction_score && (
                  <span className="text-xs text-amber-600 flex items-center gap-0.5">
                    <Star className="h-3 w-3" /> {record.satisfaction_score}/5
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {record.assigned_after_sales_id && (
              <Badge variant="outline" className="text-[10px]" data-testid="aftercare-owner">
                <User className="h-3 w-3 mr-1" />
                {record.assigned_after_sales_id === 'SYSTEM_POOL' ? 'Unassigned' : record.assigned_after_sales_id.split('@')[0] || record.assigned_after_sales_id.slice(0, 12)}
              </Badge>
            )}
            <Button variant="ghost" size="sm" className="h-7" onClick={() => setExpanded(!expanded)} data-testid="toggle-aftercare-details">
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        {record.next_action_at && record.status === 'active' && (
          <div className="flex items-center gap-1 mt-1 text-xs text-gray-500">
            <Clock className="h-3 w-3" /> Next: {formatDateLocale(record.next_action_at)}
          </div>
        )}
      </CardHeader>

      {expanded && (
        <CardContent className="pt-0 px-4 pb-4 space-y-4">
          {/* Follow-up Plan */}
          <div>
            <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">{t('patient.journey.followUpPlan')}</h4>
            <div className="space-y-1">
              {(record.followup_plan || []).map(step => (
                <FollowupStep key={step.id} step={step} canManage={canManage} onComplete={handleCompleteStep} />
              ))}
            </div>
          </div>

          {/* Complications */}
          {(record.complications || []).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-red-600 mb-2 uppercase tracking-wide">{t('patient.journey.complications')}</h4>
              <div className="space-y-1">
                {record.complications.map(comp => <ComplicationItem key={comp.id} comp={comp} />)}
              </div>
            </div>
          )}

          {/* Notes */}
          {(record.notes || []).length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">{t('patient.journey.notes')}</h4>
              <div className="space-y-1">
                {record.notes.map(note => <NoteItem key={note.id} note={note} />)}
              </div>
            </div>
          )}

          {/* Add Note */}
          {canManage && (
            <div className="flex gap-2">
              <input
                type="text"
                placeholder={t('patient.journey.addNote')}
                value={noteText}
                onChange={e => setNoteText(e.target.value)}
                className="flex-1 text-sm border rounded-md px-3 py-1.5"
                onKeyDown={e => e.key === 'Enter' && handleAddNote()}
                data-testid="aftercare-note-input"
              />
              <Button size="sm" onClick={handleAddNote} disabled={submitting || !noteText.trim()} data-testid="add-aftercare-note-btn">
                <Plus className="h-3 w-3 mr-1" /> Note
              </Button>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ── Aftercare List Panel (for embedding in dashboards) ──
export const AftercarePanel = ({ patientId, clinicId, userRole, assignedTo }) => {
  const { t } = useTranslation();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  const canManage = ['provider_admin', 'provider_after_sales_rep', 'provider_coordinator', 'provider_coordinator', 'provider_doctor', 'treatx_admin', 'treatx_super_admin', 'treatx_admin'].includes(userRole);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (patientId) params.set('patient_id', patientId);
      if (clinicId) params.set('clinic_id', clinicId);
      if (assignedTo) params.set('assigned_to', assignedTo);

      const res = await api.get(`/api/aftercare?${params.toString()}`);
      setRecords(res.data?.aftercare_records || []);
    } catch (err) {
      console.error('Failed to fetch aftercare records:', err);
    } finally {
      setLoading(false);
    }
  }, [patientId, clinicId, assignedTo]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <RefreshCw className="h-5 w-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="text-center py-6 bg-gray-50 rounded-lg" data-testid="no-aftercare-records">
        <HeartPulse className="h-8 w-8 text-gray-300 mx-auto mb-2" />
        <p className="text-sm text-gray-500">{t('patient.aftercare.empty')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="aftercare-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold flex items-center gap-2">
          <HeartPulse className="h-4 w-4 text-emerald-600" /> {t('patient.aftercare.title')} ({records.length})
        </h3>
        <Button variant="ghost" size="sm" className="h-7" onClick={fetchRecords} data-testid="refresh-aftercare-btn">
          <RefreshCw className="h-3 w-3" />
        </Button>
      </div>
      {records.map(record => (
        <AftercareCard key={record.id} record={record} canManage={canManage} onRefresh={fetchRecords} />
      ))}
    </div>
  );
};

export default AftercarePanel;
